
import { useState } from 'react';
import { useGame } from '../contexts/GameContext';
import { resetSimulation } from './simulation/resetSimulation';
import { useDecisionEvents } from './useDecisionEvents';
import { useTimeProgression } from './useTimeProgression';

export const useCareerSimulation = () => {
  const { settings } = useGame();
  
  // Default year duration to 3 minutes (180 seconds) if not set in settings
  const yearDuration = settings?.yearDuration || 180;
  
  const [decisionsThrottled, setDecisionsThrottled] = useState(false);
  const [decisionTimerFired, setDecisionTimerFired] = useState<Record<string, boolean>>({});

  // Use the specialized time progression hook
  const {
    timeState,
    timerRef,
    startTimePassage,
    canStartSimulation
  } = useTimeProgression(setDecisionsThrottled, setDecisionTimerFired);
  
  // Use the specialized decision events hook
  const {
    lastDecisionTime,
    setLastDecisionTime,
    decisionTimersRef,
    scheduleDecisionEvents,
    recentChallenges,
    setRecentChallenges
  } = useDecisionEvents(
    yearDuration,
    timeState.isSimulating,
    decisionsThrottled,
    setDecisionsThrottled,
    decisionTimerFired,
    setDecisionTimerFired
  );

  const simulateTimePassage = () => {
    if (canStartSimulation) {
      // CRITICAL FIX: Clear decision timer state before starting new simulation
      setDecisionTimerFired({});
      
      console.log('Starting new time passage simulation');
      startTimePassage(() => {
        // Schedule decision events when simulation starts
        console.log('Scheduling decision events for new simulation');
        scheduleDecisionEvents();
      });
    } else {
      console.log('Cannot start simulation:', { 
        isSimulating: timeState.isSimulating,
        currentChallenge: Boolean(useGame().currentChallenge),
        currentSpecialEvent: Boolean(useGame().currentSpecialEvent)
      });
    }
  };

  // Force reset if we detect the user might be stuck
  const forceResetSimulation = () => {
    console.log('Force resetting simulation');
    resetSimulation(
      timerRef,
      decisionTimersRef,
      timeState.isSimulating,
      timeState.yearProgress,
      setDecisionsThrottled,
      setLastDecisionTime,
      timeState.timeLeft,
      yearDuration,
      setDecisionTimerFired
    );
    
    // Also reset recent challenges
    setRecentChallenges([]);
  };

  return {
    yearProgress: timeState.yearProgress,
    timeLeft: timeState.timeLeft,
    yearDuration: timeState.yearDuration,
    isSimulating: timeState.isSimulating,
    decisionsThrottled,
    setDecisionsThrottled,
    simulateTimePassage,
    forceResetSimulation
  };
};
